import { type FormInstance } from 'antd';
import type { StoreApi } from 'zustand';

import { expiredUserPasswordForm } from '~/services/auth/AuthService';
import type { IExpiredUserPasswordParams } from '~/types/api/Auth';
import type { IAuthStore } from './useAuthStore';

export type IExpiredUserPasswordFormFilter = {
  oldPassword: string;
  password: string;
  confirmPassword: string;
};

export type IExpiredUserPasswordActions = {
  resetStates: () => void;
  setFormFilterInstance: (form: Partial<FormInstance<IExpiredUserPasswordFormFilter>>) => void;
  getParams: () => IExpiredUserPasswordParams;
  handleSubmit: () => Promise<void>;
};

export type IExpiredUserPasswordState = {
  isLoading: boolean;
  formFilterInstance: Partial<FormInstance<IExpiredUserPasswordFormFilter>> | null;
};

export type IExpiredUserPasswordSlice = IExpiredUserPasswordState & IExpiredUserPasswordActions;

const useExpiredUserPasswordSlice = (
  set: StoreApi<IAuthStore>['setState'],
  get: StoreApi<IAuthStore>['getState'],
): IExpiredUserPasswordSlice => ({
  // States
  isLoading: false,
  formFilterInstance: null,

  // Setters
  setFormFilterInstance: form => {
    get().setData(state => {
      state.expiredUserPasswordSlice.formFilterInstance = form;
    });
  },

  // Actions
  resetStates: () => {
    get().setData(state => {
      state.expiredUserPasswordSlice.isLoading = false;
      state.expiredUserPasswordSlice.formFilterInstance = null;
    });
  },
  getParams: () => {
    const { formFilterInstance } = get().expiredUserPasswordSlice;
    if (!formFilterInstance?.getFieldsValue) {
      throw new Error('Instância do formulário não está definida.');
    }

    const formValues = formFilterInstance.getFieldsValue();

    if (formValues.password !== formValues.confirmPassword) {
      throw new Error('As senhas devem ser iguais!');
    }

    const userName = sessionStorage.getItem('userName');
    if (!userName) {
      throw new Error('Nome de usuário não encontrado na sessão.');
    }

    return {
      userName,
      oldPassword: formValues.oldPassword,
      password: formValues.password,
      confirmPassword: formValues.confirmPassword,
    } as IExpiredUserPasswordParams;
  },
  handleSubmit: async () => {
    const { formFilterInstance } = get().expiredUserPasswordSlice;
    if (!formFilterInstance) return;

    get().setData(state => {
      state.expiredUserPasswordSlice.isLoading = true;
    });

    try {
      const params = get().expiredUserPasswordSlice.getParams();
      await expiredUserPasswordForm(params);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    } finally {
      get().setData(state => {
        state.expiredUserPasswordSlice.isLoading = false;
      });
    }
  },
});

export default useExpiredUserPasswordSlice;
